import React, { useCallback, useMemo } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import ChatbotNodePanel from "./ChatbotNodePanel";
import { useChatbotMutations } from "../../hooks/useChatbot";
import { hasLocalExceptionForParent, canDeleteSlot } from "./chatbotSlotParentUtils";

export function ChatbotFlowDialogSplitLayout({ slotEditorOpen, slotEditor, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isMobile) {
    return (
      <>
        {children}
        {slotEditorOpen ? slotEditor : null}
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: slotEditorOpen ? 480 : undefined, flex: 1, minWidth: 0 }}>
      <Box sx={{ flex: "1 1 auto", minWidth: 0, overflow: "auto" }}>{children}</Box>
      {slotEditorOpen ? (
        <Box
          sx={{
            flex: "0 0 420px",
            width: 420,
            maxWidth: "44%",
            borderLeft: 1,
            borderColor: "divider",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            boxShadow: (t) => t.shadows[6],
            zIndex: 1,
          }}
        >
          {slotEditor}
        </Box>
      ) : null}
    </Box>
  );
}

export default function ChatbotFlowDialogSlotEditor({
  userId,
  slotId,
  slots = [],
  onClose,
  onError,
}) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { createSlot, updateSlot, deleteSlot } = useChatbotMutations(userId);

  const slot = useMemo(
    () => (slots || []).find((s) => Number(s.id) === Number(slotId)) || null,
    [slots, slotId]
  );

  const handleSave = useCallback(
    (payload) => {
      if (!payload?.id) return;
      updateSlot.mutate(payload, {
        onSuccess: async () => {
          await queryClient.invalidateQueries(["chatbot"]);
        },
        onError: (err) => onError?.(err?.message || t("chatbotConfigPage.errorSaveSlot")),
      });
    },
    [updateSlot, queryClient, onError, t]
  );

  const handleDelete = useCallback(
    (id) => {
      if (!id) return;
      deleteSlot.mutate(id, {
        onSuccess: async () => {
          await queryClient.invalidateQueries(["chatbot"]);
          onClose?.();
        },
        onError: (err) => onError?.(err?.message || t("chatbotConfigPage.errorDeleteSlot")),
      });
    },
    [deleteSlot, queryClient, onClose, onError, t]
  );

  const handleAddChild = useCallback(() => {
    if (!slot || !userId) return;
    createSlot.mutate(
      {
        user_id: userId,
        parent_id: slot.id,
        identificador: "",
        criar_excecao: false,
        global: 0,
        ignorarglobal: 0,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(["chatbot"]);
        },
        onError: (err) => onError?.(err?.message || t("chatbotConfigPage.errorCreateSubstep")),
      }
    );
  }, [slot, userId, createSlot, queryClient, onError, t]);

  const handleAddException = useCallback(() => {
    if (!slot || !userId) return;
    createSlot.mutate(
      {
        user_id: userId,
        parent_id: slot.id,
        identificador: "Exceção",
        nome: "",
        resposta: "",
        criar_excecao: false,
        global: 0,
        ignorarglobal: 0,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(["chatbot"]);
        },
        onError: (err) => onError?.(err?.message || t("chatbotConfigPage.errorCreateSubstep")),
      }
    );
  }, [slot, userId, createSlot, queryClient, onError, t]);

  if (!slotId || !slot) return null;

  const canDelete = canDeleteSlot(slot, slots);

  return (
    <ChatbotNodePanel
      presentation={isMobile ? "overlay" : "embedded"}
      slot={slot}
      onClose={onClose}
      onSave={handleSave}
      onDelete={handleDelete}
      onAddSubpasso={slot.identificador !== "Exceção" ? handleAddChild : undefined}
      onAddException={
        slot.identificador !== "Exceção" && !hasLocalExceptionForParent(slots, slot.id)
          ? handleAddException
          : undefined
      }
      isSaving={updateSlot.isLoading}
      isDeleting={deleteSlot.isLoading}
      isAddingSubpasso={createSlot.isLoading}
      saveError={null}
      canDelete={canDelete}
    />
  );
}
